import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { AdminCoreModule, AuthenticationModule, SharedModule } from './modules';
import { CoreModule } from './core/core.module';
import { HttpClientModule, HttpParams, HTTP_INTERCEPTORS } from '@angular/common/http';
import { LoginInterceptor } from './core/infrastructure/interceptors/login.interceptor';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxGbUiKitModule } from '@geekbears/ngx-gb-ui-kit';
import { environment } from '@env/environment';
import { of } from 'rxjs';
import { APP_ROUTES } from '@typings/app-routes.constant';
import { API_ENDPOINTS } from './types';
import {
  AuthenticationDataModel,
  AuthenticationNgRxModuleConfig,
  AUTHENTICATION_NGRX_MODULE_CONFIG as GBNGX_AUTHENTICATION_CONFIG,
} from '@geekbears/gb-ngrx-auth-basic';
import {
  GbNgRxAccountBasicModuleConfig,
  GbNgRxAccountConfigToken,
} from '@geekbears/gb-ngrx-account-basic';
import { createErrorHandler } from '@sentry/angular';
import * as Sentry from '@sentry/angular';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { Router } from '@angular/router';
import { AppUserModel } from '@modules/company-manager/users/admin-users.module';

@NgModule({
  declarations: [AppComponent],
  exports: [],
  imports: [
    BrowserModule,
    AdminCoreModule,
    AuthenticationModule,
    CoreModule,
    NgxGbUiKitModule,
    HttpClientModule,
    NgxGbUiKitModule,
    BrowserAnimationsModule,
    SharedModule,
  ],
  providers: [
    // { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: true,
      }),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: environment,
      useFactory: () => {
        return environment;
      },
    },
    // { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    // ...GBNgRxAuthInterceptors,
    { provide: HTTP_INTERCEPTORS, useClass: LoginInterceptor, multi: true },
    {
      provide: GBNGX_AUTHENTICATION_CONFIG,
      useValue: {
        loginApiUrl: () => {
          return environment.apiDomain + API_ENDPOINTS.LOGIN();
        },
        authenticateOnSignup: true,
        signupApiUrl: () => {
          // not being used
          return '';
        },
        basicAuthGuardRedirect: (authData, router, route, snap) => {
          return of(
            router.parseUrl(APP_ROUTES.authentication['sign-in'].fullPath)
          );
        },
        onAuthenticatedGuardRedirect: (authData, router) => {
          let route = APP_ROUTES.dashboard.fullPath;
          return of(router.parseUrl(route));
        },
        onAuthenticatedChangeRedirect: (authenticated, router) => {
          return of(
            router.parseUrl(
              authenticated
                ? APP_ROUTES.dashboard.fullPath
                : APP_ROUTES.authentication['sign-in'].fullPath
            )
          );
        },
        refreshTokenApiUri: () => {
          return environment.apiDomain + API_ENDPOINTS.REFRESH_TOKEN();
        },
        roleBasedGuardRedirect: (authData, router, route, snap) => {
          const requiredRoles = (route.data?.['roles'] as string[]) ?? [];
          const userRoles: string[] = (authData?.user as any)?.roles ?? [];
          const isAuthorized =
            requiredRoles.length === 0 ||
            userRoles.some((role) => requiredRoles.includes(role));

          return of(
            isAuthorized ? true : router.parseUrl(APP_ROUTES.dashboard.fullPath)
          );
        },
        customFetchUserInformationResponseMapper: (data: any) => {
          const userData = data?.data ? { ...data.data } : (data ? { ...data } : {});
          if (data?.isSubscriptionBypassed !== undefined && userData.isSubscriptionBypassed === undefined) {
            userData.isSubscriptionBypassed = data.isSubscriptionBypassed;
          }
          if (userData.company?.isSubscriptionBypassed && userData.isSubscriptionBypassed === undefined) {
            userData.isSubscriptionBypassed = userData.company.isSubscriptionBypassed;
          }
          try {
            const currentStorage = JSON.parse(localStorage.getItem('s_user') || '{}');
            localStorage.setItem('s_user', JSON.stringify({ ...currentStorage, ...userData }));
          } catch (e) {
            console.error('Error syncing s_user in localStorage', e);
          }
          return Promise.resolve(new AppUserModel(userData));
        },
        fetchUserInformationAPIendpoint: (data) => {
          // const url = new URL({
          //   host: environment.apiDomain,
          //   pathname: API_ENDPOINTS.USER(data.id),
          //   searchParams: {},
          //   hash
          // });
          // return url.toString();
          const params = new HttpParams()
            .set('_id', data.id)
            .set('populate', 'profilePicture')
            .set(
              'deepPopulate',
              `{"path":"company","populate":{"path":"subscription"}}`
            );
          return (
            environment.apiDomain +
            API_ENDPOINTS.USER(data.id) +
            '?' +
            params.toString()
          );
        },
        customLoginResponseParser: async (response) => {
          const data = response.data;
          const user = data['user'] ? { ...data['user'] } : {};
          if (data['isSubscriptionBypassed'] !== undefined && user['isSubscriptionBypassed'] === undefined) {
            user['isSubscriptionBypassed'] = data['isSubscriptionBypassed'];
          }
          return new AuthenticationDataModel({
            id: user['id'] ?? user['_id'],
            user: user,
            token: data['authToken'],
            refreshToken: data['refreshToken'],
          });
        },
        customUserMapper: (data) => {
          return new AppUserModel(data);
        },
        autoLogoutOnSessionExpired: true,
        initialAuthCheckedAfterUserFetch: true,
        autoLogoutOnUnauthorizedUserInfoFetch: true,
      } as AuthenticationNgRxModuleConfig,
    },
    {
      provide: GbNgRxAccountConfigToken,
      useValue: {
        requestForgotPasswordApiUrl: () => {
          return environment.apiDomain + API_ENDPOINTS.FORGOT_PASSWORD();
        },
        setNewForgottenPasswordApiUrl: () => {
          return environment.apiDomain + API_ENDPOINTS.RESET_PASSWORD();
        },
        getUserAccountInfoApiUrl: (userId: any) => {
          return environment.apiDomain + API_ENDPOINTS.USER(userId);
        },
        changePasswordApiUrl: (userId: any) => {
          return environment.apiDomain + API_ENDPOINTS.CHANGE_PASSWORD();
        },
      } as GbNgRxAccountBasicModuleConfig,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
